/**
 * Tiny in-house i18n: 7 languages, no dependencies.
 *
 * - Detects from navigator.language on first load. English is the fallback
 *   when the primary browser language doesn't match any supported locale.
 * - Persisted in localStorage so the user's choice sticks across visits.
 * - Components subscribe via useT() and re-render when the language changes.
 */
import { useEffect, useState } from 'react';

export type Lang = 'en' | 'es' | 'nl' | 'de' | 'pt' | 'fr' | 'it';

const STRINGS = {
  en: {
    menu_hide: 'Hide menu',
    menu_show: 'Menu',
    time_of_day: 'Time of day',
    time_sunrise: 'Sunrise',
    time_noon: 'Noon',
    time_sunset: 'Sunset',
    locations: 'View presets',
    share_view: 'Share this view',
    share_copied: 'Link copied',
    your_location: 'Your location',
    locate_me: 'My Location',
    locating: 'Locating…',
    retry_gps: 'Retry GPS',
    asking_permission: 'Asking for permission…',
    stop: 'Stop',
    controls: 'Controls',
    language: 'Language',
    // Status
    status_available: 'Available',
    status_under_contract: 'Under contract',
    status_sold: 'Sold',
    status_pending: 'Pending',
    // Lot details
    lot_area: 'Area',
    lot_width: 'Width',
    lot_west: 'West',
    lot_east: 'East',
    lot_hectares: 'Hectares',
    unit_hectares: 'Hectares',
    unit_acres: 'Acres',
    lot_price: 'Price',
    lot_buyer: 'Buyer',
    lot_notes: 'Notes',
    contact_whatsapp: 'Contact via WhatsApp',
    contact_us: 'Contact Us',
    reset_view: 'Reset',
    reset_view_title: 'Reset view',
    // Offer form
    offer_make: 'Make an Offer',
    plan_download: 'Download Plan (PDF)',
    offer_name: 'Full name',
    offer_phone: 'WhatsApp number',
    offer_bid_m2: 'Bid $/m²',
    offer_bid_sft: 'Bid $/sft',
    offer_bid_lot: 'Bid $ Lot',
    offer_submit: 'Submit Offer',
    offer_cancel: 'Cancel',
    offer_submitting: 'Submitting your offer…',
    offer_received: 'Almost Done! Confirm your offer by sending us a WhatsApp message.',
    offer_confirm_wa: 'Confirm Offer',
    preset_airport: 'Airport',
    low_data_mode: 'Low Data Mode',
    mode_3d: '3D Mode',
    switch_to_3d: 'Switch back to 3D',
    // Warning banner / modal
    warning_header: 'Beware of unauthorised sellers',
    warning_tap: 'Tap for details',
    warning_p1: 'Buildings on the beach are *undocumented occupants* that do not hold any cadastral title to the land they occupy.',
    warning_p2: 'Buildings inside marked plots are *squatters* with no ownership rights of the land they occupy — they cannot legally sell you anything.',
    warning_p3: 'Avoid complete loss of your investment: only buy lots that are documented in the *public land register* (registro catastral).',
    warning_p4: 'Contact us for questions or help acquiring your lot in El Manglillo.',
    warning_got_it: 'Got it',
    // Help — desktop
    help_click: 'Click',
    help_click_desc: 'capture mouse',
    help_esc: 'Esc / Double-click',
    help_esc_desc: 'release mouse',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'move',
    help_alt: 'Right ⌥',
    help_alt_desc: 'up',
    help_cmd: 'Right ⌘',
    help_cmd_desc: 'down',
    help_wheel: 'Mouse wheel',
    help_wheel_desc: 'up / down',
    help_space: 'Space',
    help_space_desc: 'sprint',
    help_click_lot: 'Click a lot',
    help_click_lot_desc: 'see lot details',
    // Help — touch
    help_drag: 'Drag',
    help_drag_desc: 'pan',
    help_pinch: 'Pinch',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Two-finger drag',
    help_two_finger_desc: 'tilt / rotate',
    help_tap_lot: 'Tap a lot',
    help_tap_lot_desc: 'see lot details',
  },
  es: {
    menu_hide: 'Ocultar menú',
    menu_show: 'Menú',
    time_of_day: 'Hora del día',
    time_sunrise: 'Amanecer',
    time_noon: 'Mediodía',
    time_sunset: 'Atardecer',
    locations: 'Vistas',
    share_view: 'Compartir esta vista',
    share_copied: 'Enlace copiado',
    your_location: 'Tu ubicación',
    locate_me: 'Mi ubicación',
    locating: 'Localizando…',
    retry_gps: 'Reintentar GPS',
    asking_permission: 'Pidiendo permiso…',
    stop: 'Parar',
    controls: 'Controles',
    language: 'Idioma',
    status_available: 'Disponible',
    status_under_contract: 'Bajo contrato',
    status_sold: 'Vendido',
    status_pending: 'Pendiente',
    lot_area: 'Área',
    lot_width: 'Ancho',
    lot_west: 'Oeste',
    lot_east: 'Este',
    lot_hectares: 'Hectáreas',
    unit_hectares: 'Hectáreas',
    unit_acres: 'Acres',
    lot_price: 'Precio',
    lot_buyer: 'Comprador',
    lot_notes: 'Notas',
    contact_whatsapp: 'Contactar por WhatsApp',
    contact_us: 'Contáctanos',
    reset_view: 'Reset',
    reset_view_title: 'Reiniciar vista',
    offer_make: 'Hacer una oferta',
    plan_download: 'Descargar plano (PDF)',
    offer_name: 'Nombre completo',
    offer_phone: 'Número de WhatsApp',
    offer_bid_m2: 'Oferta $/m²',
    offer_bid_sft: 'Oferta $/sft',
    offer_bid_lot: 'Oferta $ Lote',
    offer_submit: 'Enviar oferta',
    offer_cancel: 'Cancelar',
    offer_submitting: 'Enviando tu oferta…',
    offer_received: '¡Ya casi! Confirma tu oferta enviándonos un mensaje de WhatsApp.',
    offer_confirm_wa: 'Confirmar oferta',
    preset_airport: 'Aeropuerto',
    low_data_mode: 'Modo bajo consumo',
    mode_3d: 'Modo 3D',
    switch_to_3d: 'Volver a la vista 3D',
    warning_header: 'Cuidado con vendedores no autorizados',
    warning_tap: 'Toca para más información',
    warning_p1: 'Las construcciones en la playa son *ocupantes sin documentación* que no tienen ningún título catastral sobre el terreno que ocupan.',
    warning_p2: 'Las construcciones dentro de lotes demarcados son *invasores* sin derechos de propiedad sobre el terreno que ocupan — no pueden venderte nada legalmente.',
    warning_p3: 'Evita la pérdida total de tu inversión: compra únicamente lotes que estén documentados en el *registro público de tierras* (registro catastral).',
    warning_p4: 'Contáctanos para preguntas o ayuda con la adquisición de tu lote en El Manglillo.',
    warning_got_it: 'Entendido',
    help_click: 'Clic',
    help_click_desc: 'capturar mouse',
    help_esc: 'Esc / Doble clic',
    help_esc_desc: 'liberar mouse',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'mover',
    help_alt: '⌥ derecho',
    help_alt_desc: 'subir',
    help_cmd: '⌘ derecho',
    help_cmd_desc: 'bajar',
    help_wheel: 'Rueda',
    help_wheel_desc: 'subir / bajar',
    help_space: 'Espacio',
    help_space_desc: 'correr',
    help_click_lot: 'Clic en un lote',
    help_click_lot_desc: 'ver detalles',
    help_drag: 'Arrastrar',
    help_drag_desc: 'desplazar',
    help_pinch: 'Pellizco',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Dos dedos',
    help_two_finger_desc: 'inclinar / rotar',
    help_tap_lot: 'Tap en un lote',
    help_tap_lot_desc: 'ver detalles',
  },
  nl: {
    menu_hide: 'Menu verbergen',
    menu_show: 'Menu',
    time_of_day: 'Tijd van de dag',
    time_sunrise: 'Zonsopkomst',
    time_noon: 'Middag',
    time_sunset: 'Zonsondergang',
    locations: 'Weergaven',
    share_view: 'Deel deze weergave',
    share_copied: 'Link gekopieerd',
    your_location: 'Je locatie',
    locate_me: 'Mijn locatie',
    locating: 'Bezig…',
    retry_gps: 'GPS opnieuw proberen',
    asking_permission: 'Toestemming vragen…',
    stop: 'Stop',
    controls: 'Bediening',
    language: 'Taal',
    status_available: 'Beschikbaar',
    status_under_contract: 'Onder contract',
    status_sold: 'Verkocht',
    status_pending: 'In behandeling',
    lot_area: 'Oppervlakte',
    lot_width: 'Breedte',
    lot_west: 'West',
    lot_east: 'Oost',
    lot_hectares: 'Hectare',
    unit_hectares: 'Hectare',
    unit_acres: 'Acres',
    lot_price: 'Prijs',
    lot_buyer: 'Koper',
    lot_notes: 'Notities',
    contact_whatsapp: 'Contact via WhatsApp',
    contact_us: 'Contact',
    reset_view: 'Reset',
    reset_view_title: 'Weergave resetten',
    offer_make: 'Bod uitbrengen',
    plan_download: 'Plan downloaden (PDF)',
    offer_name: 'Volledige naam',
    offer_phone: 'WhatsApp-nummer',
    offer_bid_m2: 'Bod $/m²',
    offer_bid_sft: 'Bod $/sft',
    offer_bid_lot: 'Bod $ Kavel',
    offer_submit: 'Bod versturen',
    offer_cancel: 'Annuleren',
    offer_submitting: 'Je bod wordt verstuurd…',
    offer_received: 'Bijna klaar! Bevestig je bod door ons een WhatsApp-bericht te sturen.',
    offer_confirm_wa: 'Bod bevestigen',
    preset_airport: 'Luchthaven',
    low_data_mode: 'Spaarstand data',
    mode_3d: '3D-modus',
    switch_to_3d: 'Terug naar 3D',
    warning_header: 'Pas op voor onbevoegde verkopers',
    warning_tap: 'Tik voor details',
    warning_p1: 'Gebouwen op het strand zijn *niet-geregistreerde bewoners* zonder kadastrale titel op het terrein.',
    warning_p2: 'Gebouwen binnen gemarkeerde kavels zijn *krakers* zonder eigendomsrechten — zij kunnen je juridisch niets verkopen.',
    warning_p3: 'Voorkom volledig verlies van je investering: koop alleen kavels die in het *openbare grondregister* (registro catastral) staan.',
    warning_p4: 'Neem contact met ons op voor vragen of hulp bij het verwerven van je kavel in El Manglillo.',
    warning_got_it: 'Begrepen',
    help_click: 'Klik',
    help_click_desc: 'muis vastleggen',
    help_esc: 'Esc / dubbelklik',
    help_esc_desc: 'muis loslaten',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'bewegen',
    help_alt: 'Rechter ⌥',
    help_alt_desc: 'omhoog',
    help_cmd: 'Rechter ⌘',
    help_cmd_desc: 'omlaag',
    help_wheel: 'Muiswiel',
    help_wheel_desc: 'omhoog / omlaag',
    help_space: 'Spatiebalk',
    help_space_desc: 'sprinten',
    help_click_lot: 'Klik op een kavel',
    help_click_lot_desc: 'details bekijken',
    help_drag: 'Slepen',
    help_drag_desc: 'verschuiven',
    help_pinch: 'Pinch',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Twee vingers',
    help_two_finger_desc: 'kantelen / draaien',
    help_tap_lot: 'Tik op een kavel',
    help_tap_lot_desc: 'details bekijken',
  },
  de: {
    menu_hide: 'Menü ausblenden',
    menu_show: 'Menü',
    time_of_day: 'Tageszeit',
    time_sunrise: 'Sonnenaufgang',
    time_noon: 'Mittag',
    time_sunset: 'Sonnenuntergang',
    locations: 'Ansichten',
    share_view: 'Diese Ansicht teilen',
    share_copied: 'Link kopiert',
    your_location: 'Dein Standort',
    locate_me: 'Mein Standort',
    locating: 'Suche…',
    retry_gps: 'GPS erneut versuchen',
    asking_permission: 'Bitte um Erlaubnis…',
    stop: 'Stopp',
    controls: 'Steuerung',
    language: 'Sprache',
    status_available: 'Verfügbar',
    status_under_contract: 'Unter Vertrag',
    status_sold: 'Verkauft',
    status_pending: 'Ausstehend',
    lot_area: 'Fläche',
    lot_width: 'Breite',
    lot_west: 'West',
    lot_east: 'Ost',
    lot_hectares: 'Hektar',
    unit_hectares: 'Hektar',
    unit_acres: 'Acres',
    lot_price: 'Preis',
    lot_buyer: 'Käufer',
    lot_notes: 'Notizen',
    contact_whatsapp: 'Per WhatsApp kontaktieren',
    contact_us: 'Kontakt',
    reset_view: 'Reset',
    reset_view_title: 'Ansicht zurücksetzen',
    offer_make: 'Angebot machen',
    plan_download: 'Plan herunterladen (PDF)',
    offer_name: 'Vollständiger Name',
    offer_phone: 'WhatsApp-Nummer',
    offer_bid_m2: 'Gebot $/m²',
    offer_bid_sft: 'Gebot $/sft',
    offer_bid_lot: 'Gebot $ Grundstück',
    offer_submit: 'Angebot senden',
    offer_cancel: 'Abbrechen',
    offer_submitting: 'Sende dein Angebot…',
    offer_received: 'Fast fertig! Bestätige dein Angebot per WhatsApp-Nachricht.',
    offer_confirm_wa: 'Angebot bestätigen',
    preset_airport: 'Flughafen',
    low_data_mode: 'Datensparmodus',
    mode_3d: '3D-Modus',
    switch_to_3d: 'Zurück zur 3D-Ansicht',
    warning_header: 'Vorsicht vor nicht autorisierten Verkäufern',
    warning_tap: 'Tippe für Details',
    warning_p1: 'Gebäude am Strand sind *nicht dokumentierte Bewohner* und haben keinen Katastertitel über das Grundstück.',
    warning_p2: 'Gebäude innerhalb markierter Parzellen sind *Hausbesetzer* ohne Eigentumsrechte — sie können dir rechtlich nichts verkaufen.',
    warning_p3: 'Vermeide den Totalverlust deiner Investition: Kaufe nur Parzellen, die im *öffentlichen Grundbuch* (registro catastral) eingetragen sind.',
    warning_p4: 'Kontaktiere uns für Fragen oder Hilfe beim Erwerb deines Grundstücks in El Manglillo.',
    warning_got_it: 'Verstanden',
    help_click: 'Klick',
    help_click_desc: 'Maus erfassen',
    help_esc: 'Esc / Doppelklick',
    help_esc_desc: 'Maus freigeben',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'bewegen',
    help_alt: 'Rechte ⌥',
    help_alt_desc: 'hoch',
    help_cmd: 'Rechte ⌘',
    help_cmd_desc: 'runter',
    help_wheel: 'Mausrad',
    help_wheel_desc: 'hoch / runter',
    help_space: 'Leertaste',
    help_space_desc: 'sprinten',
    help_click_lot: 'Klick auf Parzelle',
    help_click_lot_desc: 'Details ansehen',
    help_drag: 'Ziehen',
    help_drag_desc: 'verschieben',
    help_pinch: 'Pinch',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Zwei-Finger',
    help_two_finger_desc: 'neigen / drehen',
    help_tap_lot: 'Auf Parzelle tippen',
    help_tap_lot_desc: 'Details ansehen',
  },
  pt: {
    menu_hide: 'Ocultar menu',
    menu_show: 'Menu',
    time_of_day: 'Hora do dia',
    time_sunrise: 'Amanhecer',
    time_noon: 'Meio-dia',
    time_sunset: 'Pôr do sol',
    locations: 'Vistas',
    share_view: 'Compartilhar esta vista',
    share_copied: 'Link copiado',
    your_location: 'Sua localização',
    locate_me: 'Minha localização',
    locating: 'Localizando…',
    retry_gps: 'Tentar GPS de novo',
    asking_permission: 'Pedindo permissão…',
    stop: 'Parar',
    controls: 'Controles',
    language: 'Idioma',
    status_available: 'Disponível',
    status_under_contract: 'Sob contrato',
    status_sold: 'Vendido',
    status_pending: 'Pendente',
    lot_area: 'Área',
    lot_width: 'Largura',
    lot_west: 'Oeste',
    lot_east: 'Leste',
    lot_hectares: 'Hectares',
    unit_hectares: 'Hectares',
    unit_acres: 'Acres',
    lot_price: 'Preço',
    lot_buyer: 'Comprador',
    lot_notes: 'Notas',
    contact_whatsapp: 'Contato pelo WhatsApp',
    contact_us: 'Contato',
    reset_view: 'Reset',
    reset_view_title: 'Redefinir vista',
    offer_make: 'Fazer uma oferta',
    plan_download: 'Baixar planta (PDF)',
    offer_name: 'Nome completo',
    offer_phone: 'Número de WhatsApp',
    offer_bid_m2: 'Oferta $/m²',
    offer_bid_sft: 'Oferta $/sft',
    offer_bid_lot: 'Oferta $ Lote',
    offer_submit: 'Enviar oferta',
    offer_cancel: 'Cancelar',
    offer_submitting: 'Enviando sua oferta…',
    offer_received: 'Quase pronto! Confirme sua oferta enviando uma mensagem pelo WhatsApp.',
    offer_confirm_wa: 'Confirmar oferta',
    preset_airport: 'Aeroporto',
    low_data_mode: 'Modo de baixo consumo',
    mode_3d: 'Modo 3D',
    switch_to_3d: 'Voltar à vista 3D',
    warning_header: 'Cuidado com vendedores não autorizados',
    warning_tap: 'Toque para detalhes',
    warning_p1: 'As construções na praia são *ocupantes sem documentação* e não têm título cadastral sobre o terreno.',
    warning_p2: 'As construções dentro de lotes demarcados são *invasores* sem direitos de propriedade — não podem legalmente vender nada.',
    warning_p3: 'Evite a perda total do seu investimento: compre apenas lotes documentados no *registro público de terras* (registro catastral).',
    warning_p4: 'Entre em contato para perguntas ou ajuda na aquisição do seu lote em El Manglillo.',
    warning_got_it: 'Entendido',
    help_click: 'Clique',
    help_click_desc: 'capturar mouse',
    help_esc: 'Esc / duplo clique',
    help_esc_desc: 'liberar mouse',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'mover',
    help_alt: '⌥ direito',
    help_alt_desc: 'subir',
    help_cmd: '⌘ direito',
    help_cmd_desc: 'descer',
    help_wheel: 'Roda',
    help_wheel_desc: 'subir / descer',
    help_space: 'Espaço',
    help_space_desc: 'correr',
    help_click_lot: 'Clique em um lote',
    help_click_lot_desc: 'ver detalhes',
    help_drag: 'Arrastar',
    help_drag_desc: 'deslocar',
    help_pinch: 'Pinçar',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Dois dedos',
    help_two_finger_desc: 'inclinar / rodar',
    help_tap_lot: 'Toque em um lote',
    help_tap_lot_desc: 'ver detalhes',
  },
  fr: {
    menu_hide: 'Masquer le menu',
    menu_show: 'Menu',
    time_of_day: 'Heure du jour',
    time_sunrise: 'Lever du soleil',
    time_noon: 'Midi',
    time_sunset: 'Coucher du soleil',
    locations: 'Vues',
    share_view: 'Partager cette vue',
    share_copied: 'Lien copié',
    your_location: 'Votre position',
    locate_me: 'Ma position',
    locating: 'Localisation…',
    retry_gps: 'Réessayer GPS',
    asking_permission: 'Demande de permission…',
    stop: 'Arrêter',
    controls: 'Commandes',
    language: 'Langue',
    status_available: 'Disponible',
    status_under_contract: 'Sous contrat',
    status_sold: 'Vendu',
    status_pending: 'En attente',
    lot_area: 'Surface',
    lot_width: 'Largeur',
    lot_west: 'Ouest',
    lot_east: 'Est',
    lot_hectares: 'Hectares',
    unit_hectares: 'Hectares',
    unit_acres: 'Acres',
    lot_price: 'Prix',
    lot_buyer: 'Acheteur',
    lot_notes: 'Notes',
    contact_whatsapp: 'Contact via WhatsApp',
    contact_us: 'Contact',
    reset_view: 'Reset',
    reset_view_title: 'Réinitialiser la vue',
    offer_make: 'Faire une offre',
    plan_download: 'Télécharger le plan (PDF)',
    offer_name: 'Nom complet',
    offer_phone: 'Numéro WhatsApp',
    offer_bid_m2: 'Offre $/m²',
    offer_bid_sft: 'Offre $/sft',
    offer_bid_lot: 'Offre $ Lot',
    offer_submit: "Envoyer l'offre",
    offer_cancel: 'Annuler',
    offer_submitting: "Envoi de l'offre…",
    offer_received: 'Presque fini ! Confirmez votre offre en nous envoyant un message WhatsApp.',
    offer_confirm_wa: "Confirmer l'offre",
    preset_airport: 'Aéroport',
    low_data_mode: 'Mode économie de données',
    mode_3d: 'Mode 3D',
    switch_to_3d: 'Revenir à la vue 3D',
    warning_header: 'Méfiez-vous des vendeurs non autorisés',
    warning_tap: 'Touchez pour plus de détails',
    warning_p1: 'Les bâtiments sur la plage sont des *occupants non documentés* sans titre cadastral sur le terrain.',
    warning_p2: "Les bâtiments à l'intérieur des parcelles balisées sont des *squatters* sans droits de propriété — ils ne peuvent légalement rien vendre.",
    warning_p3: "Évitez la perte totale de votre investissement : n'achetez que des parcelles inscrites au *registre foncier public* (registro catastral).",
    warning_p4: "Contactez-nous pour toute question ou aide à l'acquisition de votre parcelle à El Manglillo.",
    warning_got_it: 'Compris',
    help_click: 'Clic',
    help_click_desc: 'capturer la souris',
    help_esc: 'Échap / double-clic',
    help_esc_desc: 'libérer la souris',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'déplacer',
    help_alt: '⌥ droit',
    help_alt_desc: 'monter',
    help_cmd: '⌘ droit',
    help_cmd_desc: 'descendre',
    help_wheel: 'Molette',
    help_wheel_desc: 'monter / descendre',
    help_space: 'Espace',
    help_space_desc: 'sprinter',
    help_click_lot: 'Cliquer un lot',
    help_click_lot_desc: 'voir les détails',
    help_drag: 'Glisser',
    help_drag_desc: 'déplacer',
    help_pinch: 'Pincer',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Deux doigts',
    help_two_finger_desc: 'incliner / tourner',
    help_tap_lot: 'Toucher un lot',
    help_tap_lot_desc: 'voir les détails',
  },
  it: {
    menu_hide: 'Nascondi menu',
    menu_show: 'Menu',
    time_of_day: 'Ora del giorno',
    time_sunrise: 'Alba',
    time_noon: 'Mezzogiorno',
    time_sunset: 'Tramonto',
    locations: 'Viste',
    share_view: 'Condividi questa vista',
    share_copied: 'Link copiato',
    your_location: 'La tua posizione',
    locate_me: 'La mia posizione',
    locating: 'Localizzazione…',
    retry_gps: 'Riprova GPS',
    asking_permission: 'Richiesta autorizzazione…',
    stop: 'Stop',
    controls: 'Controlli',
    language: 'Lingua',
    status_available: 'Disponibile',
    status_under_contract: 'Sotto contratto',
    status_sold: 'Venduto',
    status_pending: 'In sospeso',
    lot_area: 'Area',
    lot_width: 'Larghezza',
    lot_west: 'Ovest',
    lot_east: 'Est',
    lot_hectares: 'Ettari',
    unit_hectares: 'Ettari',
    unit_acres: 'Acri',
    lot_price: 'Prezzo',
    lot_buyer: 'Acquirente',
    lot_notes: 'Note',
    contact_whatsapp: 'Contatta via WhatsApp',
    contact_us: 'Contatto',
    reset_view: 'Reset',
    reset_view_title: 'Reimposta vista',
    offer_make: "Fai un'offerta",
    plan_download: 'Scarica la planimetria (PDF)',
    offer_name: 'Nome completo',
    offer_phone: 'Numero WhatsApp',
    offer_bid_m2: 'Offerta $/m²',
    offer_bid_sft: 'Offerta $/sft',
    offer_bid_lot: 'Offerta $ Lotto',
    offer_submit: 'Invia offerta',
    offer_cancel: 'Annulla',
    offer_submitting: 'Invio della tua offerta…',
    offer_received: 'Quasi fatto! Conferma la tua offerta inviandoci un messaggio WhatsApp.',
    offer_confirm_wa: 'Conferma offerta',
    preset_airport: 'Aeroporto',
    low_data_mode: 'Modalità a basso consumo',
    mode_3d: 'Modalità 3D',
    switch_to_3d: 'Torna alla vista 3D',
    warning_header: 'Attenzione ai venditori non autorizzati',
    warning_tap: 'Tocca per i dettagli',
    warning_p1: 'Le costruzioni in spiaggia sono *occupanti non documentati* senza titolo catastale sul terreno.',
    warning_p2: 'Le costruzioni dentro lotti delimitati sono *abusivi* senza diritti di proprietà — non possono venderti nulla legalmente.',
    warning_p3: 'Evita la perdita totale del tuo investimento: compra solo lotti registrati nel *registro pubblico fondiario* (registro catastral).',
    warning_p4: 'Contattaci per domande o aiuto nell\'acquisto del tuo lotto a El Manglillo.',
    warning_got_it: 'Capito',
    help_click: 'Clic',
    help_click_desc: 'cattura mouse',
    help_esc: 'Esc / doppio clic',
    help_esc_desc: 'rilascia mouse',
    help_arrows: '↑ ↓ ← →',
    help_arrows_desc: 'muovere',
    help_alt: '⌥ destro',
    help_alt_desc: 'su',
    help_cmd: '⌘ destro',
    help_cmd_desc: 'giù',
    help_wheel: 'Rotellina',
    help_wheel_desc: 'su / giù',
    help_space: 'Spazio',
    help_space_desc: 'scattare',
    help_click_lot: 'Clicca un lotto',
    help_click_lot_desc: 'vedi dettagli',
    help_drag: 'Trascina',
    help_drag_desc: 'spostare',
    help_pinch: 'Pizzica',
    help_pinch_desc: 'zoom',
    help_two_finger: 'Due dita',
    help_two_finger_desc: 'inclina / ruota',
    help_tap_lot: 'Tocca un lotto',
    help_tap_lot_desc: 'vedi dettagli',
  },
} as const;

export type Key = keyof typeof STRINGS['en'];

const LS_KEY = 'manglillo.lang';

const SUPPORTED_LANGS: ReadonlyArray<Lang> = ['en', 'es', 'nl', 'de', 'pt', 'fr', 'it'];

function detect(): Lang {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && (SUPPORTED_LANGS as readonly string[]).includes(saved)) {
      return saved as Lang;
    }
  } catch {
    // ignore (private mode etc.)
  }
  if (typeof navigator === 'undefined') return 'en';
  // Only the PRIMARY browser language counts (we ignore secondary prefs).
  // If it doesn't match a supported locale, we fall back to English.
  const primary = (navigator.languages?.[0] ?? navigator.language ?? 'en')
    .toLowerCase()
    .slice(0, 2);
  return (SUPPORTED_LANGS as readonly string[]).includes(primary)
    ? (primary as Lang)
    : 'en';
}

let currentLang: Lang = detect();
const listeners = new Set<(l: Lang) => void>();

export function getLang(): Lang {
  return currentLang;
}

export function setLang(lang: Lang) {
  if (lang === currentLang) return;
  currentLang = lang;
  try {
    localStorage.setItem(LS_KEY, lang);
  } catch {
    // ignore
  }
  for (const cb of listeners) cb(lang);
}

export function t(key: Key, lang: Lang = currentLang): string {
  return STRINGS[lang][key] ?? STRINGS.en[key] ?? key;
}

/** React hook — re-renders on language change and returns the t() function. */
export function useT() {
  const [lang, setLocal] = useState<Lang>(currentLang);
  useEffect(() => {
    const cb = (l: Lang) => setLocal(l);
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);
  return {
    lang,
    t: (key: Key) => t(key, lang),
  };
}

/**
 * Quick-contact message: "I'm interested in buying {lot name | land} in El Manglillo…"
 * Used by the always-visible Contact Us button. If `lotName` is null, it falls
 * back to the generic "land" / "un terreno".
 */
export function contactMessage(lotName: string | null, lang: Lang = currentLang): string {
  const named = !!lotName;
  switch (lang) {
    case 'es': {
      const subject = lotName ?? 'un terreno';
      return `Hola, estoy interesado en comprar ${named ? `el ${subject}` : subject} en El Manglillo. ¿Me puedes dar más información?`;
    }
    case 'nl': {
      const subject = lotName ?? 'een kavel';
      return `Hallo, ik ben geïnteresseerd in het kopen van ${named ? `kavel ${subject}` : subject} in El Manglillo. Kun je me meer vertellen?`;
    }
    case 'de': {
      const subject = lotName ?? 'einem Grundstück';
      return `Hallo, ich interessiere mich für den Kauf ${named ? `von Grundstück ${subject}` : `von ${subject}`} in El Manglillo. Könnten Sie mir mehr darüber sagen?`;
    }
    case 'pt': {
      const subject = lotName ?? 'um terreno';
      return `Olá, tenho interesse em comprar ${named ? `o lote ${subject}` : subject} em El Manglillo. Pode me dar mais informações?`;
    }
    case 'fr': {
      const subject = lotName ?? 'un terrain';
      return `Bonjour, je suis intéressé(e) par l'achat ${named ? `du lot ${subject}` : `d'${subject}`} à El Manglillo. Pouvez-vous m'en dire plus ?`;
    }
    case 'it': {
      const subject = lotName ?? 'un terreno';
      return `Ciao, sono interessato all'acquisto ${named ? `del lotto ${subject}` : `di ${subject}`} a El Manglillo. Puoi darmi più informazioni?`;
    }
    default: {
      const subject = lotName ?? 'land';
      return `Hi, I'm interested in buying ${subject} in El Manglillo. Could you tell me more?`;
    }
  }
}

/** Bid confirmation WhatsApp message body, localised. */
export function bidWhatsappMessage(args: {
  lotName: string;
  visitorName: string;
  bidLotUsd: number;
  bidM2Usd: number;
  lang?: Lang;
}): string {
  const lang = args.lang ?? currentLang;
  const lot = args.bidLotUsd.toLocaleString('en-US');
  const m2 = args.bidM2Usd.toLocaleString('en-US', { maximumFractionDigits: 2 });
  switch (lang) {
    case 'es':
      return `Hola, soy ${args.visitorName}. Quiero ofertar *US$ ${lot}* por el lote *${args.lotName}* (US$ ${m2}/m²).`;
    case 'nl':
      return `Hallo, ik ben ${args.visitorName}. Ik wil *US$ ${lot}* bieden op kavel *${args.lotName}* (US$ ${m2}/m²).`;
    case 'de':
      return `Hallo, ich bin ${args.visitorName}. Ich möchte *US$ ${lot}* für das Grundstück *${args.lotName}* bieten (US$ ${m2}/m²).`;
    case 'pt':
      return `Olá, sou ${args.visitorName}. Quero oferecer *US$ ${lot}* pelo lote *${args.lotName}* (US$ ${m2}/m²).`;
    case 'fr':
      return `Bonjour, je suis ${args.visitorName}. Je voudrais offrir *US$ ${lot}* pour le lot *${args.lotName}* (US$ ${m2}/m²).`;
    case 'it':
      return `Ciao, sono ${args.visitorName}. Vorrei offrire *US$ ${lot}* per il lotto *${args.lotName}* (US$ ${m2}/m²).`;
    default:
      return `Hi, I'm ${args.visitorName}. I'd like to bid *US$ ${lot}* on lot *${args.lotName}* (US$ ${m2}/m²).`;
  }
}

/** WhatsApp message body, localised. */
export function whatsappMessage(args: {
  lotName: string;
  status: 'available' | 'under-contract' | 'pending' | 'sold';
  areaText: string;
  priceText?: string | null;
  lang?: Lang;
}): string {
  const lang = args.lang ?? currentLang;
  const statusKey = (
    {
      available: 'status_available',
      'under-contract': 'status_under_contract',
      pending: 'status_pending',
      sold: 'status_sold',
    } as const
  )[args.status];
  const status = t(statusKey, lang);
  // Labels per language: [intro, status label, area label, price label, closing question]
  const labels: Record<Lang, [string, string, string, string, string]> = {
    es: [`Hola, me interesa el lote *${args.lotName}*.`, 'Estado', 'Área', 'Precio', '¿Me puedes enviar más información?'],
    en: [`Hi, I'm interested in *${args.lotName}*.`, 'Status', 'Area', 'Price', 'Could you send me more info?'],
    nl: [`Hallo, ik heb interesse in kavel *${args.lotName}*.`, 'Status', 'Oppervlakte', 'Prijs', 'Kun je me meer informatie sturen?'],
    de: [`Hallo, ich interessiere mich für Grundstück *${args.lotName}*.`, 'Status', 'Fläche', 'Preis', 'Können Sie mir mehr Informationen schicken?'],
    pt: [`Olá, tenho interesse no lote *${args.lotName}*.`, 'Status', 'Área', 'Preço', 'Pode me enviar mais informações?'],
    fr: [`Bonjour, je suis intéressé(e) par le lot *${args.lotName}*.`, 'Statut', 'Surface', 'Prix', 'Pouvez-vous m\'envoyer plus d\'informations ?'],
    it: [`Ciao, sono interessato al lotto *${args.lotName}*.`, 'Stato', 'Area', 'Prezzo', 'Puoi inviarmi più informazioni?'],
  };
  const [intro, statusLabel, areaLabel, priceLabel, closing] = labels[lang];
  const lines = [intro, `${statusLabel}: ${status}`, `${areaLabel}: ${args.areaText}`];
  if (args.priceText) lines.push(`${priceLabel}: ${args.priceText}`);
  lines.push('', closing);
  return lines.join('\n');
}
